import { Role } from 'constants/roles';

import { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { usePermissions } from 'hooks/usePermissions';
import { ID_FILTER_RESET_VALUES } from 'pages/Orders/constants/filterResetValues';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext, WithListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { EmptyListAlert } from 'shared/mui/Alert/EmptyListAlert/EmptyListAlert';
import { ListExportAlert } from 'shared/mui/Alert/ListExportAlert/ListExportAlert';
import { SuccessReportAlert } from 'shared/mui/Alert/SuccessReportAlert/SuccessReportAlert';
import { Button } from 'shared/mui/Buttons';
import {
  DateRangeTimezoneValue,
  DateRangeValue,
} from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterResetButton,
  FilterIdButton,
  FilterClearButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterReferenceButton } from 'shared/mui/Filter/FilterButtons/FilterReferenceButton/FilterReferenceButton';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterFavorite } from 'shared/mui/Filter/FilterFavorite/FilterFavorite';
import { FilterIdChips } from 'shared/mui/Filter/FilterId/FilterIdChips/FilterIdChips';
import { FilterIdDialog } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialog';
import { FilterIdDialogNotFoundIdAlert } from 'shared/mui/Filter/FilterId/FilterIdDialog/FilterIdDialogNotFoundIdAlert/FilterIdDialogNotFoundIdAlert';
import { FilterSaveFavorite } from 'shared/mui/Filter/FilterSaveFavorite/FilterSaveFavorite';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { FilterIdsValues, FilterIdTypes } from 'shared/mui/Filter/hooks/useIdFilters';
import { OrderList } from 'types/embedded/order';
import { cleanEmpty } from 'utils/cleanEmpty';

import { OrderListFilter } from './OrdersListFilter/OrdersListFilter';
import { OrdersListFilterChips } from './OrdersListFilterChips/OrdersListFilterChips';
import { useOrdersFilters } from './OrdersListLocalDatagrid/hooks/useOrdersFilters';
import { OrdersListLocalDatagrid } from './OrdersListLocalDatagrid/OrdersListLocalDatagrid';
import { OrdersListReferenceDialog } from './OrdersListReferenceDialog/OrdersListReferenceDialog';
import { OrdersListSummaryDialog } from './OrdersListSummaryDialog/OrdersListSummaryDialog';
import { getNotFoundIds } from './utils/getNotFoundIds';

export type OrdersListFilterFormValues = {
  appliedDate: DateRangeValue;
  createdAt?: {
    gte?: number;
    lte?: number;
  };
  amountPenny: { gte: string | null; lte: string | null };
  currency: string[];
  direction: string | null;
  merchant: string | null;
  'gateway.provider'?: string | null;
  gateway: string[];
  provider: string | null;
  method: string[];
  status: string[];
  timezone: DateRangeTimezoneValue;
  customer: string | null;
  orderProviderExternalError?: {
    providerExternalError: {
      reason: string | null;
    };
  };
  orderExternalErrorReason?: string | null;
  matchRejectReason: boolean | null;
};

type Props = {
  setIsOpenNotifyDialog: (v: boolean) => void;
};

export const OrdersListContent: React.FC<Props> = ({ setIsOpenNotifyDialog }) => {
  const translate = useTypedTranslate();

  const [isOpenSummaryDialog, setIsOpenSummaryDialog] = useState(false);
  const [isOpenIdDialog, setIsOpenIdDialog] = useState<boolean>(false);
  const [isOpenReferenceDialog, setIsOpenReferenceDialog] = useState(false);
  const [currentIdFilter, setCurrentIdFilter] = useState<FilterIdTypes>(FilterIdTypes.Any);

  const [showExportAlert, setShowExportAlert] = useState<boolean>(true);
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(true);

  const { filterValues } = useListContext();
  const { isHaveRole } = usePermissions();
  const canBulkResendNotifications = isHaveRole([Role.Admin, Role.Payment]);

  const { filtersForm, idFiltersForm } = useOrdersFilters();

  const { form, appliedFilters, setAppliedFilter, toggleDrawer, openDrawer, onSubmit, onReset } =
    filtersForm;
  const {
    form: formId,
    appliedIdFilters,
    setAppliedFilter: setAppliedIdFilter,
    onSubmit: onSubmitIds,
    onReset: onResetIds,
  } = idFiltersForm;

  const isListFilterExist = Object.keys(filterValues).length > 0;
  const isDateFilterExist = !!filterValues.createdAt?.gte && !!filterValues.createdAt?.lte;

  const isIdFormValuesExist = () => Object.values(cleanEmpty(formId.getValues())).length > 0;

  const resetIdFilters = () => {
    formId.reset(ID_FILTER_RESET_VALUES);
    onSubmitIds(ID_FILTER_RESET_VALUES);
  };

  const handleSubmitFilters = (values: OrdersListFilterFormValues) => {
    const isFilterValuesExist = Object.values(cleanEmpty(values)).length > 0;
    isFilterValuesExist && resetIdFilters();
    onSubmit(values);
  };

  const setAppliedFavoriteFilter = (name: string, value: unknown) => {
    resetIdFilters();
    setAppliedFilter(name, value);
  };

  const handleSubmitIds = (values: FilterIdsValues) => {
    isIdFormValuesExist() && onReset();
    onSubmitIds(values);
  };

  useEffect(() => {
    setShowExportAlert(true);
    setShowSuccessAlert(true);
  }, [appliedFilters, appliedIdFilters]);

  return (
    <WithListContext
      render={({ isFetching, data: orders = [] as OrderList[], total }) => {
        const {
          notFoundIds,
          notFoundMerchantReferenceIds,
          notFoundProviderReferenceIds,
          notFoundAnyIds,
        } = getNotFoundIds(orders as OrderList[], appliedIdFilters);

        return (
          <>
            <FilterToolbar
              leftActionsSlot={[
                <FilterIdButton
                  key="filter-by-id"
                  onClick={() => {
                    !isIdFormValuesExist() && setCurrentIdFilter(FilterIdTypes.Any);
                    setIsOpenIdDialog(true);
                  }}
                />,
                <FilterButton key="filter" onClick={toggleDrawer(true)} />,
                <FilterContextProvider
                  appliedFilters={appliedFilters}
                  key="filter-favourite"
                  setAppliedFilter={setAppliedFavoriteFilter}
                >
                  <FormProvider {...form}>
                    <FilterFavorite />
                  </FormProvider>
                </FilterContextProvider>,
                <FilterColumnsButton
                  disabled={!orders.length || !isListFilterExist}
                  key="select-column"
                />,
                canBulkResendNotifications && (
                  <FilterReferenceButton
                    key="reference"
                    onClick={() => setIsOpenReferenceDialog(true)}
                  />
                ),
              ]}
            />
            <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
              <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSubmitFilters)}>
                  <FilterDrawerContent>
                    <FilterContextProvider
                      appliedFilters={appliedFilters}
                      setAppliedFilter={setAppliedFilter}
                    >
                      <OrderListFilter />
                    </FilterContextProvider>
                  </FilterDrawerContent>
                  <FilterDrawerActionsButtons
                    topSlot={<FilterSaveFavorite disabled={!isListFilterExist} />}
                  >
                    <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
                    <FilterApplyButton type="submit" />
                  </FilterDrawerActionsButtons>
                </form>
              </FormProvider>
            </FilterDrawer>
            <FilterChipsToolbar appliedFilters={appliedFilters} appliedIdFilters={appliedIdFilters}>
              <FilterContextProvider
                appliedFilters={appliedFilters}
                setAppliedFilter={setAppliedFilter}
              >
                <FormProvider {...form}>
                  <OrdersListFilterChips />
                </FormProvider>
              </FilterContextProvider>
              <FilterContextProvider
                appliedFilters={appliedIdFilters}
                setAppliedFilter={setAppliedIdFilter}
              >
                <FormProvider {...formId}>
                  <FilterIdChips
                    onClick={(type) => {
                      setIsOpenIdDialog(true);
                      setCurrentIdFilter(type);
                    }}
                  />
                  <FilterClearButton
                    key={`clear-${currentIdFilter}`}
                    onClick={() => {
                      onReset();
                      onResetIds(currentIdFilter);
                    }}
                    visible={isListFilterExist}
                  />
                </FormProvider>
              </FilterContextProvider>
            </FilterChipsToolbar>
            <FormProvider {...formId}>
              <FilterIdDialog
                currentIdFilter={currentIdFilter}
                notFoundAnyIds={notFoundAnyIds}
                notFoundIds={notFoundIds}
                notFoundMerchantIds={notFoundMerchantReferenceIds}
                notFoundProviderIds={notFoundProviderReferenceIds}
                onChange={(currentFilter) => setCurrentIdFilter(currentFilter)}
                onClose={() => setIsOpenIdDialog(false)}
                onReset={onResetIds}
                onSubmit={handleSubmitIds}
                open={isOpenIdDialog}
              />
            </FormProvider>
            {(total || 0) >= 1001 && !isFetching && isListFilterExist && showExportAlert && (
              <ListExportAlert
                onClick={() => setIsOpenNotifyDialog(true)}
                onClose={() => setShowExportAlert(false)}
                sx={{ margin: '4px 0 10px 0' }}
              />
            )}
            <FilterIdDialogNotFoundIdAlert ids={notFoundIds} isFetching={!!isFetching} />
            <FilterIdDialogNotFoundIdAlert
              ids={notFoundMerchantReferenceIds}
              isFetching={!!isFetching}
            />
            <FilterIdDialogNotFoundIdAlert
              ids={notFoundProviderReferenceIds}
              isFetching={!!isFetching}
            />
            <FilterIdDialogNotFoundIdAlert ids={notFoundAnyIds} isFetching={!!isFetching} />
            <Box
              sx={{
                minHeight: '100%',
                display: 'initial',
              }}
            >
              {!isListFilterExist && (
                <EmptyListAlert
                  message={translate('app.orders.toGenerateReportSelect')}
                  onClick={toggleDrawer(true)}
                />
              )}
              {!!orders?.length && showSuccessAlert && isDateFilterExist && !isFetching && (
                <SuccessReportAlert
                  actionButton={
                    <Button
                      color="inherit"
                      key="summary-button"
                      label={translate('app.orders.summaryForPeriod')}
                      onClick={() => setIsOpenSummaryDialog(true)}
                      size="small"
                      variant="text"
                    />
                  }
                  date={filterValues?.createdAt}
                  onClick={() => setIsOpenSummaryDialog(true)}
                  onClose={() => setShowSuccessAlert(false)}
                  sx={{ marginBottom: '16px', marginTop: '4px' }}
                  timezone={filterValues?.timezone}
                />
              )}
              {isListFilterExist && (
                <OrdersListLocalDatagrid
                  isLoading={!!isFetching}
                  orders={(orders as OrderList[]) || []}
                  timezone={filterValues?.timezone}
                  total={total || 0}
                />
              )}
              {isOpenSummaryDialog && (
                <OrdersListSummaryDialog
                  filterValues={filterValues}
                  onClose={() => setIsOpenSummaryDialog(false)}
                  open={isOpenSummaryDialog}
                />
              )}
              {isOpenReferenceDialog && (
                <OrdersListReferenceDialog
                  onClose={() => setIsOpenReferenceDialog(false)}
                  open={isOpenReferenceDialog}
                />
              )}
            </Box>
          </>
        );
      }}
    />
  );
};
